/**
 * 39. Customers — list; detail with balance, credit limit, contacts,
 * addresses, transaction history, status block.
 *
 * Arabic and English (C7, defect D61: this screen stayed English when the
 * interface was Arabic): every word from the catalogs; a customer's type and
 * status are the glossary's words, never the stored value; amounts go through
 * the locale's formatter with Latin digits.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
} from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { valueLabel } from "@/lib/i18n/records";
import { formatMoney } from "@/lib/money";
import { CreateRecordButton } from "@/components/CreateRecordButton";
import { useCurrencyFormField } from "@/components/CurrencyField";

interface CustomerRow {
  id: string;
  customerNo: string;
  name: string;
  customerType: "Individual" | "Company";
  currencyCode: string;
  creditLimit: string | null;
  openBalance: string;
  status: "Active" | "Blocked" | "Dormant";
}

export function CustomersPage() {
  const { t } = useTranslation();
  // Prefilled with the workspace currency, never a literal (CUR-2).
  const currencyField = useCurrencyFormField("currencyCode", true);
  const { data, isLoading } = useApiList<CustomerRow>(
    ["sales", "customers"],
    "/sales/customers",
  );

  return (
    <div>
      <PageHeader
        title={t("sales.customers.title")}
        subtitle={t("sales.customers.subtitle")}
        actions={
          <CreateRecordButton
            label={t("sales.customers.new")}
            path="/customers"
            invalidate={["sales", "customers"]}
            fields={[
              {
                name: "customerNo",
                label: t("sales.customers.customerNumber"),
                required: true,
              },
              { name: "name", label: t("fields.name"), required: true },
              currencyField,
            ]}
          />
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={listTitle(t("sales.customers.list"), data.total)}>
          <DataTable<CustomerRow>
            rows={data.items}
            columns={[
              {
                key: "customerNo",
                label: t("fields.number"),
                render: (r) => (
                  <span className="atlas-mono">{r.customerNo}</span>
                ),
              },
              { key: "name", label: t("fields.name") },
              {
                key: "customerType",
                label: t("fields.type"),
                render: (r) =>
                  r.customerType
                    ? valueLabel(t, "customerType", r.customerType)
                    : "—",
              },
              {
                key: "creditLimit",
                label: t("sales.customers.creditLimit"),
                numeric: true,
                render: (r) =>
                  r.creditLimit
                    ? formatMoney(r.creditLimit, r.currencyCode)
                    : "—",
              },
              {
                key: "openBalance",
                label: t("sales.customers.openBalance"),
                numeric: true,
                render: (r) => formatMoney(r.openBalance, r.currencyCode),
              },
              {
                key: "status",
                label: t("fields.status"),
                render: (r) => (
                  <StatusBadge status={r.status} type="Customer" />
                ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
